import { reactive } from 'vue'
import { has, set, get, map, unset } from 'lodash'

/**
 * ErrorObject interface to define the structure of error objects.
 */
interface ErrorObject {
  data?: {
    title?: string;
    detail?: string;
    violations?: Array<{ propertyPath: string; title: string }>;
  };
}

/**
 * Provides error handling functionality using Vue's Composition API.
 * @returns {Object} Methods and reactive state for handling form errors and validation.
 */
export function useErrorHandler () {
  // Reactive state for violations
  const violations = reactive<Record<string, string[]>>({})

  /**
   * Build violations from the error object.
   * @param {ErrorObject} err - Error object containing the violations.
   */
  function buildViolations (err: ErrorObject): void {
    if (!has(err, 'data.violations')) {
      return
    }

    const newViolations: Record<string, string[]> = {}

    map(err.data!.violations, (value) => {
      set(newViolations, value.propertyPath, value.title)
    })

    Object.assign(violations, newViolations)
  }

  /**
   * Build a violation at the custom propertyPath.
   * @param {ErrorObject} err - Error object.
   * @param {string} propertyPath - The property path to set the violation.
   */
  function buildViolation (err: ErrorObject, propertyPath: string): void {
    let message = ''

    if (has(err, 'data.title')) {
      message += err.data!.title
    }

    if (has(err, 'data.detail')) {
      message += `: ${err.data!.detail}`
    }

    if (!has(violations, propertyPath)) {
      set(violations, propertyPath, [])
    }

    (violations[propertyPath] as string[]).push(message)
  }

  /**
   * Clear all violations.
   */
  function clearViolations (): void {
    Object.keys(violations).forEach((key) => {
      unset(violations, key)
    })
  }

  /**
   * Clear a violation at the given property path.
   * @param {string} propertyPath - The property path to clear the violation.
   */
  function clearViolation (propertyPath: string): void {
    unset(violations, propertyPath)
  }

  /**
   * Check if there is a violation for the given propertyPath.
   * @param {string} propertyPath - The property path to check.
   * @returns {boolean} Whether a violation exists at the propertyPath.
   */
  function hasViolation (propertyPath: string): boolean {
    if (!propertyPath) {
      return false
    }

    return has(violations, propertyPath)
  }

  /**
   * Get the violation at the property path.
   * @param {string} propertyPath - The property path to get the violation.
   * @returns {string | boolean} The violation message or false if not found.
   */
  function getViolation (propertyPath: string): string | boolean {
    if (!propertyPath) {
      return false
    }

    const violation = get(violations, propertyPath)

    // Handle cases where the violation might be an array
    if (Array.isArray(violation)) {
      return violation.join(', ') // Join array elements into a single string
    }

    return violation || false // Return the value or false if undefined
  }

  /**
   * Get multiple violations at the given property paths.
   * @param {string[]} propertyPaths - The property paths to get the violations.
   * @returns {Record<string, string | undefined>} An object of violations.
   */
  function getViolations (propertyPaths: string[]): Record<string, string | undefined> {
    if (!propertyPaths) {
      return {}
    }

    const result: Record<string, string | undefined> = {}

    map(propertyPaths, (value) => {
      set(result, value, get(violations, value))
    })

    return result
  }

  return {
    violations,
    buildViolations,
    buildViolation,
    clearViolations,
    clearViolation,
    hasViolation,
    getViolation,
    getViolations
  }
}
